/**
 * CalendarHeaderCell - Cel·la de capçalera per cada dia
 * Inclou: número del dia, dia setmana, i espai per events/festes
 *
 * Colors plans de `CALENDAR_PALETTE` (`plans/calendar-visual-refresh.md` §3): normal, DV-DS (D5) i
 * avui (amb la línia vertical de 2 px a la vora esquerra, que continua pel cos de la graella).
 * L'alçada la fixa la fila de la capçalera (`GRID_HEADER_HEIGHT` a `CalendarGrid`).
 *
 * Mobile-first, modular
 */

import { cn } from '@/lib/utils';
import { longDayTitle } from './grid/day-labels';
import { CALENDAR_PALETTE } from './calendar-theme';

export interface DayEvent {
  id: string;
  type: 'holiday' | 'event' | 'note' | 'warning';
  label: string;
  color?: string;
}

export interface CalendarHeaderCellProps {
  date: Date;
  isToday?: boolean;
  /** Divendres o dissabte (D5): les nits fortes. */
  isWeekend?: boolean;
  events?: DayEvent[];
  onClick?: (date: Date) => void;
  className?: string;
  style?: React.CSSProperties;
  cellWidth?: number;
}

// Colors per tipus d'event
const EVENT_COLORS: Record<DayEvent['type'], string> = {
  holiday: 'bg-red-100 text-red-700 border-red-200',
  event: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-blue-100 text-blue-700 border-blue-200',
  warning: 'bg-orange-100 text-orange-700 border-orange-200',
};

// Icones per tipus
const EVENT_ICONS: Record<DayEvent['type'], string> = {
  holiday: '🎉',
  event: '📅',
  note: '📝',
  warning: '⚠️',
};

export function CalendarHeaderCell({
  date,
  isToday = false,
  isWeekend = false,
  events = [],
  onClick,
  className,
  style,
  cellWidth = 200,
}: CalendarHeaderCellProps) {
  const dayNumber = date.getDate();
  const weekday = date.toLocaleDateString('ca-ES', { weekday: 'short' }).toUpperCase();

  const handleClick = () => {
    onClick?.(date);
  };

  // Forçar amplada amb style inline
  const cellStyle: React.CSSProperties = {
    ...style,
    width: `${cellWidth}px`,
    minWidth: `${cellWidth}px`,
    maxWidth: `${cellWidth}px`,
    flexBasis: `${cellWidth}px`,
  };

  const background = isToday
    ? CALENDAR_PALETTE.headerToday
    : isWeekend
      ? CALENDAR_PALETTE.headerWeekend
      : CALENDAR_PALETTE.header;

  return (
    <div
      onClick={handleClick}
      title={longDayTitle(date)}
      data-today={isToday || undefined}
      data-weekend={isWeekend || undefined}
      style={{ ...cellStyle, backgroundColor: background, borderRightColor: CALENDAR_PALETTE.separator }}
      className={cn(
        // Base - IMPORTANT: flex-shrink-0 i flex-grow-0 per evitar que flex comprimeixi
        'relative flex h-full flex-col p-1.5 sm:p-2 flex-shrink-0 flex-grow-0',
        'border-r transition-colors duration-200',
        'touch-manipulation box-border',

        // Cursor
        onClick && 'cursor-pointer',

        className
      )}
    >
      {/* Avui: línia de 2 px a la vora esquerra; baixa per sobre de la vora de la capçalera. */}
      {isToday && (
        <span
          aria-hidden="true"
          data-testid="today-line"
          className="pointer-events-none absolute left-0 top-0 -bottom-0.5 w-0.5"
          style={{ backgroundColor: CALENDAR_PALETTE.todayLine }}
        />
      )}

      {/* Day Number */}
      <div
        className={cn(
          'text-center font-bold text-base sm:text-lg leading-tight',
          !isToday && 'text-gray-700'
        )}
        style={isToday ? { color: CALENDAR_PALETTE.headerTodayText } : undefined}
      >
        {dayNumber}
      </div>

      {/* Weekday */}
      <div
        className={cn(
          'text-center text-[10px] sm:text-xs font-medium uppercase tracking-tight',
          !isToday && 'text-gray-600'
        )}
        style={isToday ? { color: CALENDAR_PALETTE.headerTodayText } : undefined}
      >
        {weekday}
      </div>

      {/* Events/Holidays: un sol badge (l'alçada de la capçalera és fixa) i «+N» per a la resta */}
      <div className="mt-1 min-h-[16px] flex items-center gap-0.5 min-w-0 overflow-hidden">
        {events.length > 0 && <EventBadge event={events[0]} />}
        {events.length > 1 && (
          <span
            className="text-[9px] text-gray-500 shrink-0"
            title={events.slice(1).map((e) => e.label).join(' · ')}
          >
            +{events.length - 1}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * EventBadge - Mini badge per events dins del header
 */
function EventBadge({ event }: { event: DayEvent }) {
  return (
    <div
      className={cn(
        'min-w-0 flex-1 text-[8px] sm:text-[9px] px-1 py-0.5 rounded truncate',
        'border leading-none text-center',
        EVENT_COLORS[event.type]
      )}
      title={event.label}
    >
      <span className="mr-0.5">{EVENT_ICONS[event.type]}</span>
      <span className="hidden sm:inline">{event.label}</span>
    </div>
  );
}

export default CalendarHeaderCell;
